import React, { useEffect, useState } from "react";
import { useLang } from "@/context/LangContext";
import api, { getErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import { Trash2, Pencil, X, Check, Download } from "lucide-react";
import { downloadCSV } from "@/lib/csv";

const BOOKING_STATUSES = ["pending", "confirmed", "rescheduling", "finished", "cancelled"];
const STATUS_TONE = {
  pending: "bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] border-[hsl(var(--border))]",
  confirmed: "bg-[hsl(var(--secondary))]/10 text-[hsl(var(--secondary))] border-[hsl(var(--secondary))]/30",
  rescheduling: "bg-[hsl(var(--primary))]/10 text-[hsl(var(--primary))] border-[hsl(var(--primary))]/30",
  finished: "bg-[hsl(var(--foreground))]/5 text-[hsl(var(--foreground))]/60 border-[hsl(var(--border))]",
  cancelled: "bg-[hsl(var(--destructive))]/10 text-[hsl(var(--destructive))] border-[hsl(var(--destructive))]/30",
};
const BOOKING_TYPES = ["interview", "trial_class", "consultant"];
const BOOKING_PROGRAMS = ["Primary", "K-12", "Teens", "IB", "Cambridge KET/PET/FCE", "A-Level"];

export default function BookingsPanel() {
  const { t } = useLang();
  const [items, setItems] = useState([]);
  const [statusFilter, setStatusFilter] = useState("_all");
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  const [savingId, setSavingId] = useState(null);

  async function load() {
    try { const { data } = await api.get("/bookings"); setItems(data); } catch { /* ignore */ }
  }
  useEffect(() => { (async () => { await load(); })(); }, []);

  const filtered = statusFilter === "_all" ? items : items.filter((b) => (b.status || "pending") === statusFilter);

  function startEdit(b) {
    setEditingId(b.id);
    setDraft({
      booking_type: b.booking_type,
      name: b.name || "",
      email: b.email || "",
      phone: b.phone || "",
      program: b.program || "",
      preferred_date: b.preferred_date || "",
      preferred_time: b.preferred_time || "",
      notes: b.notes || "",
      status: b.status || "pending",
    });
  }
  function cancelEdit() { setEditingId(null); setDraft(null); }

  async function quickStatus(id, status) {
    setSavingId(id);
    try {
      const { data } = await api.patch(`/bookings/${id}`, { status });
      setItems((prev) => prev.map((x) => x.id === id ? { ...x, ...data } : x));
      toast.success(`Marked as ${status}`);
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setSavingId(null); }
  }

  async function saveEdit() {
    if (!draft || !editingId) return;
    setSavingId(editingId);
    try {
      const { data } = await api.patch(`/bookings/${editingId}`, draft);
      setItems((prev) => prev.map((x) => x.id === editingId ? { ...x, ...data } : x));
      toast.success("Booking updated");
      cancelEdit();
    } catch (e) { toast.error(getErrorDetail(e)); }
    finally { setSavingId(null); }
  }

  async function del(id) {
    if (!window.confirm("Delete this booking permanently?")) return;
    try {
      await api.delete(`/bookings/${id}`);
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success("Deleted");
      if (editingId === id) cancelEdit();
    } catch (e) { toast.error(getErrorDetail(e)); }
  }

  function setDraftField(k, v) { setDraft((d) => ({ ...d, [k]: v })); }

  function exportCSV() {
    if (items.length === 0) { toast.error("No bookings to export"); return; }
    const rows = (statusFilter === "_all" ? items : filtered).map((b) => ({
      created_at: format(parseISO(b.created_at), "yyyy-MM-dd HH:mm"),
      booking_type: b.booking_type,
      name: b.name,
      email: b.email,
      phone: b.phone || "",
      program: b.program || "",
      preferred_date: b.preferred_date || "",
      preferred_time: b.preferred_time || "",
      status: b.status || "pending",
      notes: b.notes || "",
    }));
    const stamp = format(new Date(), "yyyyMMdd-HHmm");
    const tag = statusFilter === "_all" ? "all" : statusFilter;
    downloadCSV(`dawnville-bookings-${tag}-${stamp}.csv`, rows, [
      { key: "created_at", label: "Created" },
      { key: "booking_type", label: "Type" },
      { key: "name", label: "Name" },
      { key: "email", label: "Email" },
      { key: "phone", label: "Phone / WeChat" },
      { key: "program", label: "Program" },
      { key: "preferred_date", label: "Date" },
      { key: "preferred_time", label: "Time" },
      { key: "status", label: "Status" },
      { key: "notes", label: "Notes" },
    ]);
    toast.success(`Exported ${rows.length} booking(s)`);
  }

  return (
    <div data-testid="admin-bookings-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <h3 className="text-xs uppercase tracking-[0.25em] text-[hsl(var(--muted-foreground))]">{t.admin.bookings} · {items.length}</h3>
        <div className="flex items-center gap-2 flex-wrap">
          <Button size="sm" variant="outline" onClick={exportCSV} disabled={items.length === 0} data-testid="admin-bookings-export-csv" className="rounded-full"><Download className="w-3.5 h-3.5 mr-1.5" />Export CSV</Button>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger data-testid="admin-bookings-status-filter" className="w-56"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="_all" data-testid="admin-bookings-filter-all">All statuses</SelectItem>
              {BOOKING_STATUSES.map((s) => (
                <SelectItem key={s} value={s} data-testid={`admin-bookings-filter-${s}`}>{s.charAt(0).toUpperCase() + s.slice(1)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="bg-white border border-[hsl(var(--border))] rounded-2xl max-h-[70vh] overflow-y-auto" data-testid="admin-bookings-scroll">
        <table className="w-full text-sm" data-testid="admin-bookings-table">
          <thead className="sticky top-0 bg-white z-10 border-b border-[hsl(var(--border))]">
            <tr className="text-left text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">
              <th className="p-4 font-normal">When</th>
              <th className="p-4 font-normal">Type</th>
              <th className="p-4 font-normal">Name</th>
              <th className="p-4 font-normal">Contact</th>
              <th className="p-4 font-normal">Program · slot</th>
              <th className="p-4 font-normal">Status</th>
              <th className="p-4 font-normal text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((b) => editingId === b.id ? (
              <tr key={b.id} data-testid={`admin-booking-row-${b.id}`} className="bg-[hsl(var(--muted))]/40 border-b border-[hsl(var(--border))]/60">
                <td colSpan={7} className="p-5">
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
                    <div className="md:col-span-3">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Type</Label>
                      <Select value={draft.booking_type} onValueChange={(v)=>setDraftField("booking_type", v)}>
                        <SelectTrigger data-testid={`bk-${b.id}-type`} className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>{BOOKING_TYPES.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Status</Label>
                      <Select value={draft.status} onValueChange={(v)=>setDraftField("status", v)}>
                        <SelectTrigger data-testid={`bk-${b.id}-status`} className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {BOOKING_STATUSES.map(x=><SelectItem key={x} value={x} data-testid={`bk-${b.id}-status-${x}`}>{x.charAt(0).toUpperCase() + x.slice(1)}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Program</Label>
                      <Select value={draft.program} onValueChange={(v)=>setDraftField("program", v)}>
                        <SelectTrigger data-testid={`bk-${b.id}-program`} className="mt-2"><SelectValue /></SelectTrigger>
                        <SelectContent>{BOOKING_PROGRAMS.map(x=><SelectItem key={x} value={x}>{x}</SelectItem>)}</SelectContent>
                      </Select>
                    </div>
                    <div className="md:col-span-3">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Name</Label>
                      <Input value={draft.name} onChange={(e)=>setDraftField("name", e.target.value)} data-testid={`bk-${b.id}-name`} className="mt-2" />
                    </div>
                    <div className="md:col-span-4">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Email</Label>
                      <Input type="email" value={draft.email} onChange={(e)=>setDraftField("email", e.target.value)} data-testid={`bk-${b.id}-email`} className="mt-2" />
                    </div>
                    <div className="md:col-span-4">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Phone / WeChat</Label>
                      <Input value={draft.phone} onChange={(e)=>setDraftField("phone", e.target.value)} data-testid={`bk-${b.id}-phone`} className="mt-2" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Date</Label>
                      <Input type="date" value={draft.preferred_date} onChange={(e)=>setDraftField("preferred_date", e.target.value)} data-testid={`bk-${b.id}-date`} className="mt-2" />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Time</Label>
                      <Input placeholder="HH:MM" value={draft.preferred_time} onChange={(e)=>setDraftField("preferred_time", e.target.value)} data-testid={`bk-${b.id}-time`} className="mt-2" />
                    </div>
                    <div className="md:col-span-12">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[hsl(var(--muted-foreground))]">Notes</Label>
                      <Textarea rows={2} value={draft.notes} onChange={(e)=>setDraftField("notes", e.target.value)} data-testid={`bk-${b.id}-notes`} className="mt-2" />
                    </div>
                    <div className="md:col-span-12 flex items-center gap-2 pt-1">
                      <Button onClick={saveEdit} disabled={savingId === b.id} data-testid={`bk-${b.id}-save`} className="rounded-full bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90 text-white"><Check className="w-4 h-4 mr-1.5" />Save</Button>
                      <Button variant="outline" onClick={cancelEdit} data-testid={`bk-${b.id}-cancel`} className="rounded-full"><X className="w-4 h-4 mr-1.5" />Cancel</Button>
                      <span className="flex-1" />
                      <Button variant="ghost" onClick={()=>del(b.id)} data-testid={`bk-${b.id}-delete`} className="rounded-full text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4 mr-1.5" />Delete</Button>
                    </div>
                  </div>
                </td>
              </tr>
            ) : (
              <tr key={b.id} data-testid={`admin-booking-row-${b.id}`} className="border-b border-[hsl(var(--border))]/60 last:border-0 hover:bg-[hsl(var(--muted))]/30">
                <td className="p-4 text-[hsl(var(--muted-foreground))] whitespace-nowrap">{format(parseISO(b.created_at), "MMM dd HH:mm")}</td>
                <td className="p-4 capitalize">{(b.booking_type || "").replace("_", " ")}</td>
                <td className="p-4 font-medium">{b.name}</td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">
                  <div>{b.email}</div>
                  {b.phone && <div className="text-xs">{b.phone}</div>}
                </td>
                <td className="p-4 text-[hsl(var(--muted-foreground))]">
                  <div>{b.program}</div>
                  <div className="text-xs">{b.preferred_date} · {b.preferred_time}</div>
                </td>
                <td className="p-4">
                  <Select value={b.status || "pending"} onValueChange={(v)=>quickStatus(b.id, v)} disabled={savingId === b.id}>
                    <SelectTrigger
                      data-testid={`admin-booking-status-${b.id}`}
                      className={`h-8 rounded-full text-xs px-3 border ${STATUS_TONE[b.status || "pending"]}`}
                    ><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {BOOKING_STATUSES.map((s)=> (
                        <SelectItem key={s} value={s} data-testid={`admin-booking-status-${b.id}-${s}`}>{s.charAt(0).toUpperCase()+s.slice(1)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </td>
                <td className="p-4 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={()=>startEdit(b)} data-testid={`admin-booking-edit-${b.id}`}><Pencil className="w-4 h-4" /></Button>
                  <Button size="sm" variant="ghost" onClick={()=>del(b.id)} data-testid={`admin-booking-delete-${b.id}`} className="text-[hsl(var(--destructive))]"><Trash2 className="w-4 h-4" /></Button>
                </td>
              </tr>
            ))}
            {filtered.length === 0 && <tr><td colSpan={7} className="p-10 text-center text-[hsl(var(--muted-foreground))]">No bookings match this filter.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
